import { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import { shallowEqual } from 'tangle-shared'
import useRouter from 'hooks/useRouter'
import autosize from 'autosize'
import { useStoreSelector, useShallowSelector } from 'helpers/reactReduxHelpers'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { previousUrlsSelector } from 'selectors/navigationSelectors'
import { makeUrlRelative } from 'helpers/utilsHelpers'
import { ComposeState, ComposeId } from 'interfaces/feed'
import { createComposeSelector } from 'selectors/feedComposeSelectors'
import { CategorizedPage } from 'interfaces/pages'
import { createSelectedCategoryIdSelector } from 'selectors/globalSelectors'
import { State } from 'interfaces/state'

// changes object link when it doesn't matches previous object (with shallowEqual)
export function useShallowMemo<T> (data: T) {
  const dataRef = useRef<T>(data)

  if (!shallowEqual(dataRef.current, data)) {
    dataRef.current = data
  }

  return dataRef.current
}

export function useNativeHistory () {
  const { history } = useRouter()
  const previousUrls = useShallowSelector(previousUrlsSelector)

  return {
    hasHistory: previousUrls.length > 0,
    history,
  }
}

export function useAutosize (inputNode: HTMLTextAreaElement | null) {
  useEffect(() => {
    if (!inputNode) {
      return
    }

    autosize(inputNode)
    return () => {
      autosize.destroy(inputNode)
    }
  }, [inputNode])

  return useCallback(() => {
    if (!inputNode) {
      return
    }
    autosize.update(inputNode)
  }, [inputNode])
}

type SidebarPopupPlacePosition = 'left' | 'right' | 'fill'
export function usePopupPlacePosition (): SidebarPopupPlacePosition {
  const viewportSegment = useStoreSelector(viewportSegmentSelector)

  const position: SidebarPopupPlacePosition = viewportSegment === 'desktop'
    ? 'right'
    : viewportSegment === 'tablet'
      ? 'left'
      : 'fill'

  return position
}

export function useCurrentUrl () {
  const router = useRouter()

  return makeUrlRelative(router.location)
}

export function useMatchMedia (mediaQuery: string) {
  const [isQueryMatch, setIsQueryMatch] = useState(false)

  const handleMatchesChange = useCallback<(event: MediaQueryListEvent) => void>((e) => {
    setIsQueryMatch(e.matches)
  }, [])

  useEffect(() => {
    const mediaMatch = window.matchMedia(mediaQuery)

    mediaMatch.addListener(handleMatchesChange)

    setIsQueryMatch(window.matchMedia(mediaQuery).matches)

    return () => {
      mediaMatch.removeListener(handleMatchesChange)
    }
  }, [mediaQuery, handleMatchesChange])

  return isQueryMatch
}

export function useComposeState<TDefaultValue> (composeId: ComposeId, defaultValue: TDefaultValue): ComposeState | TDefaultValue {
  const withComposeId = useMemo(() => ({
    composeId,
  }), [composeId])

  const composeSelector = useMemo(createComposeSelector, [])
  const composeState = useShallowSelector((state) => {
    return composeSelector(state, withComposeId) || defaultValue
  })

  return composeState
}

export const useSelectedCategoryId = <TCategorisedPage extends CategorizedPage>(type: TCategorisedPage): State['app'][TCategorisedPage]['categories']['selectedId'] => {
  const selectedCategoryIdSelector = useMemo(createSelectedCategoryIdSelector, [])
  return useStoreSelector((state) => selectedCategoryIdSelector(state, type))
}
